import { useLocalization } from "@fluent/react";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { formatNumber } from "@narmi/design_system";
import ApiHttp from "byzantine/src/ApiHttp";
import { useNotificationContext } from "cerulean";
import SectionCard from "../SectionCard";

interface HoldsCardProps {
  accountUuid: string;
}

type Hold = {
  id: string;
  description: string;
  amount: number;
  expires_at?: string;
};

const HoldsCard = ({ accountUuid }: HoldsCardProps) => {
  const { l10n } = useLocalization();
  const [holds, setHolds] = useState<Hold[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const { sendNotification } = useNotificationContext();

  const fetchHolds = async () => {
    try {
      setIsLoading(true);
      const response = await ApiHttp.fetch(`accounts/${accountUuid}/holds`, {
        method: "GET",
      });
      if (response?.holds) {
        setHolds(response.holds);
      }
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("error-holds"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHolds();
  }, []);

  return (
    <SectionCard
      isLoading={isLoading}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
      kind={isExpanded ? "transactions" : undefined}
      title={l10n.getString("heading-holds", null, "Holds")}
    >
      {holds.length !== 0 && (
        <>
          <div className="card-table three">
            <div className="row-wrapper">
              <div className="row-item-header">{l10n.getString("transactions-table-header-description")}</div>
              <div className="row-item-header align-right">{l10n.getString("transactions-table-header-amount")}</div>
              <div className="row-item-header">{l10n.getString("transactions-table-header-expiration")}</div>
            </div>
            {holds.map((hold) => (
              <div key={hold.id} className="row-wrapper">
                <div className="row-items-column">
                  <div className="row-item">{hold.description}</div>
                </div>
                <div className="row-items-column">
                  <div className="row-item align-right number">
                    {formatNumber(hold.amount / 100)}
                  </div>
                  <div className="row-item align-right responsive-text-styling">
                    {hold.expires_at
                      ? DateTime.fromISO(hold.expires_at)
                        .toUTC()
                        .toFormat("MM/dd/yyyy")
                      : l10n.getString("not-available", null, "N/A")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </SectionCard>
  );
};
export default HoldsCard;
